import type { supabase as supabaseClient } from '../supabase';
import { buildSourcePieChartPayload, type SourcePieChartPayload } from './sourcePieChart';
import type { LeadsBySourceReportType } from './leadsBySourceReport';

interface OpportunityRow {
  source: string | null;
  lost_reason: string | null;
  value: unknown;
}

/** Group opportunities by either `source` or `lost_reason`, with a fallback bucket. */
function groupOpportunities(
  opportunities: OpportunityRow[] | null,
  field: 'source' | 'lost_reason',
  unknownLabel: string,
): SourcePieChartPayload {
  const buckets = new Map<string, { display: string; count: number; value: number }>();

  for (const opp of opportunities ?? []) {
    const raw = (opp[field] as string | null)?.trim();
    const display = raw && raw.length > 0 ? raw : unknownLabel;
    const key = display.toLowerCase();
    const value = parseFloat(String(opp.value ?? 0)) || 0;
    const existing = buckets.get(key);

    if (existing) {
      existing.count += 1;
      existing.value += value;
    } else {
      buckets.set(key, { display, count: 1, value });
    }
  }

  const entries = Array.from(buckets.values()).map((row) => ({
    source: row.display,
    count: row.count,
    value: row.value,
  }));

  return buildSourcePieChartPayload(entries);
}

export async function fetchLeadsBySourcePie(
  supabase: typeof supabaseClient,
  businessId: string,
  reportType: LeadsBySourceReportType,
  rangeFromInclusive: string,
  rangeBeforeExclusive: string,
): Promise<SourcePieChartPayload> {
  let query = supabase
    .from('pipeline_opportunities')
    .select('source, lost_reason, value')
    .eq('business_id', businessId);

  if (reportType === 'new') {
    query = query
      .not('created_at', 'is', null)
      .gte('created_at', rangeFromInclusive)
      .lt('created_at', rangeBeforeExclusive);
  } else {
    const status = reportType === 'won' ? 'Won' : 'Lost';
    query = query
      .eq('status', status)
      .gte('updated_at', rangeFromInclusive)
      .lt('updated_at', rangeBeforeExclusive);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  if (reportType === 'lost') {
    return groupOpportunities(data as OpportunityRow[] | null, 'lost_reason', 'No reason recorded');
  }

  return groupOpportunities(data as OpportunityRow[] | null, 'source', 'Unknown');
}
